import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';

export default class ScrollAnimate extends React.Component {
  static isOffsetAboveScreen(yOffset) {
    return yOffset < window.pageYOffset;
  }

  static isOffsetBelowScreen(yOffset) {
    return yOffset > window.pageYOffset + window.innerHeight;
  }

  static isRangeOnScreen(yTop, yBottom) {
    return !ScrollAnimate.isOffsetAboveScreen(yBottom) && !ScrollAnimate.isOffsetBelowScreen(yTop);
  }

  static visibilityHasChanged(oldVisibility, newVisibility) {
    return (
      oldVisibility.onScreen !== newVisibility.onScreen ||
      oldVisibility.inViewport !== newVisibility.inViewport
    );
  }

  constructor(props) {
    super(props);

    this.isServer = typeof window === 'undefined';
    this.listener = throttle(this.handleScroll.bind(this), 50);
    this.visibility = {
      onScreen: false,
      inViewport: false,
    };

    // Initial styles for node to be animated
    this.styleDefault = {
      opacity: props.initiallyVisible ? 1 : 0,
    };

    // Styles for node while animating, opacity controlled by animation
    this.styleAnimating = {
      animationDuration: `${props.duration}ms`,
    };

    // Styles for node after animated in, fully opaque
    this.styleAnimatedIn = {
      opacity: 1,
    };

    // Styles for node after animated out, fully transparent
    this.styleAnimatedOut = {
      opacity: 0,
    };

    this.state = {
      classes: 'animated',
      style: this.styleDefault,
      isAnimatedIn: false,
    };

    if (!this.isServer) {
      if (window && window.addEventListener) {
        window.addEventListener('scroll', this.listener);
      }
    }
  }

  componentDidMount() {
    if (!this.isServer) {
      this.handleScroll();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.animationDelayTimeout);
    clearTimeout(this.animationDurationTimeout);

    if (window && window.removeEventListener) {
      window.removeEventListener('scroll', this.listener);
    }
  }

  getElementTop() {
    let yOffset = 0;
    let element = this.node;
    while (element) {
      yOffset += element.offsetTop + element.clientTop;
      element = element.offsetParent;
    }

    return yOffset;
  }

  getViewportBottom() {
    return window.pageYOffset + window.innerHeight - this.props.offset;
  }

  getViewportTop() {
    return window.pageYOffset + this.props.offset;
  }

  getVisibility() {
    const elementTop = this.getElementTop();
    const elementBottom = elementTop + this.node.clientHeight;
    return {
      onScreen: ScrollAnimate.isRangeOnScreen(elementTop, elementBottom),
      inViewport: this.isRangeInViewport(elementTop, elementBottom),
    };
  }

  isOffsetAboveViewport(yOffset) {
    return yOffset < this.getViewportTop();
  }

  isOffsetBelowViewport(yOffset) {
    return yOffset > this.getViewportBottom();
  }

  isOffsetInViewport(yOffset) {
    const offsetViewportTop = this.getViewportTop();
    const offsetViewportBottom = this.getViewportBottom();
    return yOffset >= offsetViewportTop && yOffset <= offsetViewportBottom;
  }

  isRangeInViewport(yTop, yBottom) {
    return (
      this.isOffsetInViewport(yTop) ||
      this.isOffsetInViewport(yBottom) ||
      (this.isOffsetAboveViewport(yTop) && this.isOffsetBelowViewport(yBottom))
    );
  }

  animate(animation, callback) {
    const { delay, duration } = this.props;
    this.animationDelayTimeout = setTimeout(() => {
      this.animating = true;
      this.setState({
        classes: `animated ${animation}`,
        style: this.styleAnimating,
      });
      this.animationDurationTimeout = setTimeout(callback, duration);
    }, delay);
  }

  animateIn() {
    this.animate(this.props.animateIn, () => {
      if (!this.props.animateOnce) {
        this.setState({
          style: this.styleAnimatedIn,
          isAnimatedIn: true,
        });
        this.animating = false;
      }
    });
  }

  animateOut = () => {
    this.animate(this.props.animateOut, () => {
      this.setState({
        classes: 'animated',
        style: this.styleAnimatedOut,
        isAnimatedIn: false,
      });
      const visibility = this.getVisibility();
      if (visibility.inViewport && this.props.animateIn) {
        this.animateIn();
      } else {
        this.animating = false;
      }
    });
  };

  handleScroll() {
    if (!this.animating) {
      const oldVisibility = this.visibility;
      const currentVisibility = this.getVisibility();
      if (ScrollAnimate.visibilityHasChanged(oldVisibility, currentVisibility)) {
        clearTimeout(this.animationDelayTimeout);
        if (!currentVisibility.onScreen) {
          // node is off screen
          this.setState({
            classes: 'animated',
            style: this.styleDefault,
          });
        } else if (currentVisibility.inViewport && this.props.animateIn) {
          // node has entered viewport
          this.animateIn();
        } else if (
          currentVisibility.onScreen &&
          this.visibility.inViewport &&
          this.props.animateOut &&
          this.state.isAnimatedIn
        ) {
          // node was in viewport but has moved out of viewport
          this.animateOut();
        }

        this.visibility = currentVisibility;
      }
    }
  }

  render() {
    return (
      <div
        ref={node => {
          this.node = node;
        }}
        className={`${this.state.classes} ${this.props.classes}`}
        style={this.state.style}
      >
        {this.props.children}
      </div>
    );
  }
}

ScrollAnimate.defaultProps = {
  animateOnce: false,
  animateIn: '',
  animateOut: '',
  children: '',
  classes: '',
  delay: 0,
  duration: 1000,
  initiallyVisible: false,
  offset: 100,
};

ScrollAnimate.propTypes = {
  animateOnce: PropTypes.bool,
  animateIn: PropTypes.string,
  animateOut: PropTypes.string,
  children: PropTypes.node,
  classes: PropTypes.string,
  delay: PropTypes.number,
  duration: PropTypes.number,
  initiallyVisible: PropTypes.bool,
  offset: PropTypes.number,
};



// WEBPACK FOOTER //
// ./src/components/ScrollAnimate.js